/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import {
  isEmpty,
  isNumber,
  kebabCase,
  orderBy,
  partition,
  reduceRight,
  startCase,
  sumBy
} from 'lodash';

import {
  isActivePlus,
  isPershingProduct,
  isMutualFund,
  productMapByNumber,
  productTypeNameFromNumber,
  products,
  productTypePriority,
  ACTIVE_PLUS,
  ACTIVE_ADVISORY,
  MUTUAL_FUND,
  BROKERAGE,
  NATIONAL_529,
  MARYLAND_529,
  ALASKA_529
} from 'services/Products';

import { buildPositionDetails } from 'services/Formats';

import { extractAndFormatOwners } from 'services/Accounts';

import { calculatePreviousBalance, percentChange } from 'services/Calculation';

const CASH_ROW_NAME = 'Cash';
export const VIEW_ONLY = 'viewOnly';

export const ownershipTypes = {
  VIEW_ONLY: 'viewOnly',
  RELATED: 'related',
  OWNED: 'owned'
};

export const tableTypes = {
  HEADER_ROW: Symbol('HEADER_ROW'),
  ACCOUNT_ROW: Symbol('ACCOUNT_ROW'),
  HOLDING_ROW: Symbol('HOLDING_ROW')
};

export const metaDataTypes = {
  SHOW: Symbol('show'),
  HIDE: Symbol('hide'),
  LINKED: Symbol('linked-text'),
  STATIC: Symbol('static-text')
};

export const ownershipMapping = ({ owner, isViewOnly, isRelated }) =>
  owner
    ? ownershipTypes.OWNED
    : isRelated
    ? ownershipTypes.RELATED
    : isViewOnly
    ? ownershipTypes.VIEW_ONLY
    : '';

export const retirementMapping = ({ retirementAccount }) =>
  retirementAccount ? 'Is-Retirement' : 'Non-Retirement';
export const nonRetirementMapping = ({ retirementAccount }) =>
  !retirementAccount ? 'Non-Retirement' : 'Is-Retirement';

export const PRODUCT = 'product';
export const OWNERSHIP = 'ownership';
export const RETIREMENT = 'retirement';
export const NON_RETIREMENT = 'nonRetirement';
const SELECTION_ARRAY = [PRODUCT, OWNERSHIP];

export const isProductOrOwnershipSelection = (selection) =>
  SELECTION_ARRAY.includes(selection);
export const isProductSelection = (selection) => selection === PRODUCT;
export const isOwnershipSelection = (selection) => selection === OWNERSHIP;
export const accountGrouping = ({ accountType }) => [accountType];
export const isRetirementSelection = (selection) => selection === RETIREMENT;
export const isNonRetirementSelection = (selection) =>
  selection === NON_RETIREMENT;

export const ownerGrouping = ({ owners }) => {
  const [primary, secondary] = partition(owners, 'primaryOwner');
  return [primary.map((o) => o.ownerName), secondary.map((o) => o.ownerName)];
};

export const beneficiaryGrouping = ({ beneficiaries }) => {
  return beneficiaries.map((beneficiary) => beneficiary.name);
};

export const groupingFunctionalMapping = (name, isSorting = false) => {
  const mapping = {
    default: isSorting
      ? [
          ({ retirementAccount }) => !retirementAccount,
          'accountType',
          ownerGrouping
        ]
      : (account) => [
          !account.retirementAccount,
          accountGrouping(account),
          ownerGrouping(account)
        ],
    retirement: isSorting
      ? [
          ({ retirementAccount }) => !retirementAccount,
          'accountType',
          ownerGrouping
        ]
      : retirementMapping,
    nonRetirement: isSorting
      ? [
          ({ retirementAccount }) => retirementAccount,
          'accountType',
          ownerGrouping
        ]
      : nonRetirementMapping,
    product: ({ productType }) => {
      const productTypeName = productTypeNameFromNumber(productType);
      return productTypePriority.indexOf(productTypeName);
    },
    ownership: isSorting ? [ownershipMapping, ownerGrouping] : ownershipMapping
  };

  return mapping[name];
};

// A truly unqiue value we denote as "empty"
const empty = Symbol('empty');

// Filter on our empty, because the existing code may have expectioations for `Null` and `Undefined`
const filterOutEmpty = (acc, value, key) =>
  value === empty ? acc : { [key]: value, ...acc };

export const orderAccountTableKeys = ({
  accountId = empty,
  ticker = empty,
  accountWChecksum = empty,
  ownerNames = empty,
  ...rest
}) =>
  reduceRight(
    // place the necessary values in the new order, without disturbing anything else
    { accountId, ticker, accountWChecksum, ownerNames },
    filterOutEmpty,
    rest
  );

export const includeMetaData =
  ({ hasCollegeSavingsOnly }) =>
  (transformedAccount) => {
    const {
      productType,
      accountId,
      ownershipType,
      asOfDate,
      ticker,
      ownerNames,
      groupLabel,
      cusip
    } = transformedAccount.details.name;
  const isViewOnlyBrokerageHolding = transformedAccount.details.isViewOnly;
    const isHolding = transformedAccount._type === tableTypes.HOLDING_ROW;
    const isHeader = transformedAccount._type === tableTypes.HEADER_ROW;
    return {
      ...transformedAccount,
      metaData: {
        accountName:
          ownershipType === ownershipTypes.VIEW_ONLY || isHolding
            ? metaDataTypes.STATIC
            : metaDataTypes.LINKED,
        quantity: {
          extraDecimal: isPershingProduct(productType)
            ? metaDataTypes.SHOW
            : metaDataTypes.HIDE
        },
        holdings: isPershingProduct(productType)
          ? metaDataTypes.SHOW
          : metaDataTypes.HIDE,
        ticker:
          isEmpty(ticker) || isActivePlus(productType) || (isHolding && cusip && !isViewOnlyBrokerageHolding)
            ? metaDataTypes.HIDE
            : !isHolding &&
              isMutualFund(productType) &&
              !isEmpty(accountId) &&
              ownershipType !== ownershipTypes.VIEW_ONLY
            ? metaDataTypes.LINKED
            : metaDataTypes.STATIC,
        cusip:
          isHolding && isEmpty(ticker)
            ? metaDataTypes.SHOW
            : metaDataTypes.HIDE,
        accountLink:
          (isHolding && (!isPershingProduct(productType) || (isPershingProduct(productType)) && !cusip))
          || (ownershipType === ownershipTypes.VIEW_ONLY || isViewOnlyBrokerageHolding)
            ? metaDataTypes.HIDE
            : metaDataTypes.SHOW,
        asOfDate:
          isHolding || isEmpty(asOfDate)
            ? metaDataTypes.HIDE
            : metaDataTypes.SHOW,
        ownerNames:
          (!isHeader && hasCollegeSavingsOnly) ||
          isEmpty(ownerNames || (groupLabel && groupLabel.owners))
            ? metaDataTypes.HIDE
            : metaDataTypes.SHOW,
        actions:
          isHolding || isHeader || transformedAccount.details.isViewOnly
            ? metaDataTypes.HIDE
            : metaDataTypes.SHOW
      }
    };
  };
export const mapAccountsToTableData =
  ({
    filteringSelections,
    currentDate,
    view,
    transformAccount,
    isProductOrOwnership,
    accountPerformanceMap = {}
  }) =>
  (accounts) => {
    const includeMetaDataPreped = includeMetaData({
      filteringSelections,
      hasCollegeSavingsOnly: view.hasCollegeSavingsOnly
    });
    const accountsInAccountTypeGroup = buildPositionDetails(
      accounts,
      currentDate,
      accountPerformanceMap
    )
      .map(includeMetaDataPreped)
      .map((account) =>
        isPershingProduct(account.details.productType)
          ? {
              ...account,
              children: account.children.map(
                includeMetaData({
                  filteringSelections,
                  hasCollegeSavingsOnly: view.hasCollegeSavingsOnly
                })
              )
            }
          : account
      )
      .map(transformAccount);
    const accountsMarketValue = sumBy(accountsInAccountTypeGroup, (account) => {
      const { details } = account;
      return details && isNumber(details.marketValue) ? details.marketValue : 0;
    });

    const accountsTotalDifference =
      sumBy(accountsInAccountTypeGroup, 'details.dailyChange.difference') || 0;

    const accountsPreviousMarketValue = calculatePreviousBalance(
      accountsMarketValue,
      accountsTotalDifference
    );

    const accountsTotalDailyChange = {
      difference: accountsTotalDifference,
      percentChange: percentChange(
        accountsMarketValue,
        accountsPreviousMarketValue
      )
    };

    const detailsAccountInfo = groupingNameMapping({
      details: accounts[0],
      grouping: filteringSelections.grouping.selection,
      hasCollegeSavingsOnly: view.hasCollegeSavingsOnly,
      isProductOrOwnership
    });

    return includeMetaDataPreped({
      _type: tableTypes.HEADER_ROW,
      details: {
        name: detailsAccountInfo,
        marketValue: accountsMarketValue,
        dailyChange: accountsTotalDailyChange,
        balance: accountsMarketValue
      },
      children: sortRowObjects(
        filteringSelections.sorting.selection,
        accountsInAccountTypeGroup
      )
    });
  };

const groupingClassNameSubGroup = (details, grouping, isProductOrOwnership) => {
  const subGroup = isProductOrOwnership ? '-subgroup' : '';
  const mapping = {
    ownership: ownershipMapping(details),
    retirement: retirementMapping(details),
    nonRetirement: nonRetirementMapping(details)
  };

  if (grouping === 'retirement') {
    return `${kebabCase(mapping[grouping])}${subGroup}`;
  }

  if (grouping === 'nonRetirement') {
    return `${kebabCase(mapping[grouping])}${subGroup}`;
  }

  if (grouping === 'ownership') {
    return `${kebabCase(mapping[grouping])}-accounts${subGroup}`;
  }

  return `${productTypeNameFromNumber(details.productType)}${subGroup}`;
};

export const groupingNameMapping = ({
  details,
  grouping,
  hasCollegeSavingsOnly,
  isProductOrOwnership
}) => {
  const mapping = {
    default: {
      groupLabel: {
        accountType: details.accountType,
        owners: extractAndFormatOwners(details)
      },
      groupingClassName: hasCollegeSavingsOnly
        ? productMapByNumber[details.productType]
        : details.retirementAccount
        ? 'is-retirement'
        : 'non-retirement'
    },
    retirement: {
      groupLabel: isProductOrOwnership
        ? {
            accountType: details.accountType,
            owners: extractAndFormatOwners(details)
          }
        : details.retirementAccount
        ? 'Retirement'
        : 'Non-Retirement',
      groupingClassName: `${groupingClassNameSubGroup(
        details,
        grouping,
        isProductOrOwnership
      )}`
    },
    nonRetirement: {
      groupLabel: isProductOrOwnership
        ? {
            accountType: details.accountType,
            owners: extractAndFormatOwners(details)
          }
        : !details.retirementAccount
        ? 'Non-Retirement'
        : 'Retirement',
      groupingClassName: `${groupingClassNameSubGroup(
        details,
        grouping,
        isProductOrOwnership
      )}`
    },
    product: {
      groupLabel: isProductOrOwnership
        ? {
            accountType: details.accountType,
            owners: extractAndFormatOwners(details)
          }
        : products.byAccount[details.productType].label,
      groupingClassName: `${groupingClassNameSubGroup(
        details,
        grouping,
        isProductOrOwnership
      )}`
    },
    ownership: {
      groupLabel: isProductOrOwnership
        ? {
            accountType: details.accountType,
            owners: extractAndFormatOwners(details)
          }
        : startCase(ownershipMapping(details)) + ' Accounts',
      groupingClassName: `${groupingClassNameSubGroup(
        details,
        grouping,
        isProductOrOwnership
      )}`
    }
  };

  return mapping[grouping];
};

const sortRowObjects = (sorting, rowObjects) => {
  const sortingSpecs = sortingPathMapping(sorting).map(
    (spec) => 'details.' + spec
  );
  const shouldReverse = !['default', 'ticker'].includes(sorting); // Strings are sorted A-Z, numbers are sorted high-follow

  return orderBy(rowObjects, sortingSpecs, [
    shouldReverse ? 'desc' : 'asc',
    'asc'
  ]).map(({ children = [], ...rowObj }) => {
    const sortedChildren = orderBy(
      children.filter((c) => !c.details.isCashRow),
      sortingSpecs,
      [shouldReverse ? 'desc' : 'asc', 'asc']
    ); // APP and Brokerage holdings are just the details so no need for 'details.'
    return {
      ...rowObj,
      children: [
        ...sortedChildren,
        ...children.filter((c) => c.details.isCashRow) // will either return a single value or an empty array that is flattened into nothing, whereas `find` would return `undefined`
      ]
    };
  });
};

const sortingPathMapping = (name) => {
  const mapping = {
    default: [
      'name.ownerNames.primaryOwner',
      'name.ownerNames.secondaryOwner',
      'name.accountName'
    ],
    ticker: ['name.ticker', 'name.accountName'],
    dailyChange: ['dailyChange.difference', 'name.accountName']
  };

  return mapping[name] || [name, 'name.accountName'];
};

export const sortBy = (rowNodes, sortOrder) => {
  const productTypeOrder = [
    ACTIVE_PLUS,
    ACTIVE_ADVISORY,
    MUTUAL_FUND,
    BROKERAGE,
    NATIONAL_529,
    MARYLAND_529,
    ALASKA_529
  ];
  const ownershipOrder = ['Owned', 'Related', 'View Only'];
  let nextInsertPos = rowNodes.length - 1;

  if (sortOrder === 'desc') {
    nextInsertPos = 0;
  }

  rowNodes.forEach((node, i) => {
    // Move all Retirement row nodes to top of grid
    if (
      node.group &&
      node.field === 'retirementAccount' &&
      node.groupData['ag-Grid-AutoColumn'] === 'true'
    ) {
      rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
      nextInsertPos++;
    }
  });

  //Want to put CASH at the bottom of position data.
  const isPositionRows =
    rowNodes &&
    rowNodes.length &&
    rowNodes[0] &&
    rowNodes[0].data &&
    rowNodes[0].data.isPosition;
  if (isPositionRows) {
    rowNodes.forEach((node, i) => {
      if (node && node.data && node.data.accountName === CASH_ROW_NAME) {
        rowNodes.splice(rowNodes.length, 0, rowNodes.splice(i, 1)[0]);
      }
    });
  }

  if (sortOrder === 'productType') {
    productTypeOrder.forEach((productType) => {
      rowNodes.forEach((node, i) => {
        if (
          node.group &&
          node.field === sortOrder &&
          node.groupData['ag-Grid-AutoColumn'] === productType.toString()
        ) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      });
    });
  }

  if (sortOrder === 'isOwner') {
    ownershipOrder.forEach((ownershipType) => {
      rowNodes.forEach((node, i) => {
        if (
          node.group &&
          node.field === sortOrder &&
          node.groupData['ag-Grid-AutoColumn'] === ownershipType
        ) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      });
    });
  }

  return rowNodes;
};
